import React from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { CogIcon } from '@heroicons/react/24/outline';
import { ConfigCard } from './ConfigCard';
import { NestedConfigField } from './NestedConfigField';
import { useAuthStore } from '../../store/authStore';
import { getPortfolioCategoryConfig } from '../../services/api';

// Define field ordering for specific categories
const FIELD_ORDER: Record<string, string[]> = {
  lifecycle: ['trade_count_thresholds', 'allocation_caps', 'confidence_modifiers'],
  account_moves: ['hierarchy_behavior', 'promotion_thresholds', 'demotion_thresholds'],
  // Other categories will use alphabetical order or sort_order from API
};

interface CategoryCardProps {
  categoryKey: string;
  categoryName: string;
  categoryDescription: string;
  icon: React.ComponentType<{ className?: string }>;
  isExpanded: boolean;
  onToggle: () => void;
  onValueChange: (path: string, value: any) => void;
  onRemoveOverride: (path: string) => void;
  onResetCategory: (category: string) => void;
  isUpdating?: boolean;
}

export const CategoryCard: React.FC<CategoryCardProps> = ({
  categoryKey,
  categoryName,
  categoryDescription,
  icon,
  isExpanded,
  onToggle,
  onValueChange,
  onRemoveOverride,
  onResetCategory,
  isUpdating = false
}) => {
  const { isApiAuthReady } = useAuthStore();

  // Fetch data for this specific category - ALWAYS fetch for status calculation
  const {
    data: categoryConfig,
    isLoading: isLoadingCategoryConfig,
    error: categoryConfigError
  } = useQuery({
    queryKey: ['portfolio-category-config', categoryKey],
    queryFn: () => getPortfolioCategoryConfig(categoryKey),
    enabled: !!isApiAuthReady, // Always fetch data for status calculation
    staleTime: 10 * 60 * 1000, // Increase to 10 minutes to avoid overriding optimistic updates
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    retry: 2,
    refetchOnMount: false, // Don't refetch on mount if data is fresh
    refetchOnWindowFocus: false, // Don't refetch when window gains focus
  });

  // Calculate status based on actual data
  const getActualStatus = () => {
    if (!categoryConfig) {
      return {
        status: 'default' as const,
        overrideCount: 0,
        hasOverrides: false
      };
    }

    let overrideCount = 0;
    if (categoryConfig.configs) {
      overrideCount = Object.values(categoryConfig.configs).filter(
        (config: any) => config.has_user_override
      ).length;
    }

    if (overrideCount > 0) {
      return {
        status: 'customized' as const,
        overrideCount,
        hasOverrides: true
      };
    }

    return {
      status: 'default' as const,
      overrideCount: 0,
      hasOverrides: false
    };
  };

  const status = getActualStatus();

  const categoryData = {
    id: categoryKey,
    name: categoryName,
    description: categoryDescription,
    icon: icon,
    configs: categoryConfig?.configs || {},
    hasOverrides: status.hasOverrides,
    overrideCount: status.overrideCount,
    status: status.status
  };

  return (
    <ConfigCard
      category={categoryData}
      isExpanded={isExpanded}
      onToggle={onToggle}
      onConfigureClick={onToggle}
    >
      {/* Card Content - Configuration Fields */}
      <div className="space-y-4">
        {isLoadingCategoryConfig && isExpanded ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/5 rounded-lg p-4 animate-pulse">
                <div className="h-4 bg-white/10 rounded mb-2"></div>
                <div className="h-8 bg-white/10 rounded"></div>
              </div>
            ))}
          </div>
        ) : categoryConfigError && isExpanded ? (
          <div className="text-center py-8 text-red-400">
            <CogIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Failed to load configuration for {categoryName}</p>
            <p className="text-xs text-white/60 mt-1">
              {categoryConfigError.message}
            </p>
          </div>
        ) : categoryConfig && Object.keys(categoryConfig.configs || {}).length > 0 ? (
          <div className="space-y-6">
            {/* Configuration Fields - Multi-column layout when expanded */}
            <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
              {Object.entries(categoryConfig.configs || {})
                .sort(([a], [b]) => {
                  const fieldOrder = FIELD_ORDER[categoryKey];
                  if (fieldOrder) {
                    const indexA = fieldOrder.indexOf(a);
                    const indexB = fieldOrder.indexOf(b);
                    // If both fields are in the order array, use their positions
                    if (indexA !== -1 && indexB !== -1) {
                      return indexA - indexB;
                    }
                    // If only one is in the order array, it comes first
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                  }
                  // Fall back to alphabetical order
                  return a.localeCompare(b);
                })
                .map(([configKey, configData]: [string, any]) => (
                  <NestedConfigField
                    key={configKey}
                    fieldKey={configKey}
                    fieldData={{
                      value: configData.value,
                      default_value: configData.default_value,
                      description: configData.description,
                      validation_rules: configData.validation_rules,
                      has_user_override: configData.has_user_override,
                      display_unit: configData.display_unit,
                      unit_type: configData.unit_type
                    }}
                    onValueChange={(path, value) => {
                      console.log(`🏷️ CategoryCard passing categoryKey: "${categoryKey}" for configKey: "${configKey}"`);
                      onValueChange(path, value);
                    }}
                    onRemoveOverride={onRemoveOverride}
                    categoryKey={categoryKey}
                    isUpdating={isUpdating}
                  />
                ))}
            </div>

            {/* Category Reset Button */}
            {categoryConfig.has_any_overrides && (
              <div className="pt-6 border-t border-white/10">
                <div className="flex justify-center">
                  <button
                    onClick={() => onResetCategory(categoryKey)}
                    className="inline-flex items-center px-6 py-3 text-sm font-medium text-red-400 border border-red-400/30 rounded-lg hover:bg-red-400/10 transition-colors shadow-lg backdrop-blur-sm"
                  >
                    <motion.div
                      className="w-4 h-4 mr-2"
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, ease: "easeInOut" }}
                    >
                      ↻
                    </motion.div>
                    Reset {categoryName} to Defaults
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : isExpanded ? (
          <div className="text-center py-8 text-white/60">
            <CogIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No configuration options available for {categoryName}.</p>
          </div>
        ) : null}
      </div>
    </ConfigCard>
  );
};
